import { createAsyncThunk } from '@reduxjs/toolkit';
import { http } from '../../../helpers/request';

const PROJECT_PRODUCT_TAGS_TTL_MS = 10 * 1000; // 10 seconds

type StateWithProjectProductTagReducer = {
    projectProductTagReducer: { byProjectProductId: Record<number, { fetchedAt?: number }> };
};

const baseUrl = '/ProjectProductTags';

export const getProjectProductTagsByProjectProductIdAction = createAsyncThunk<
    any,
    number,
    { state: StateWithProjectProductTagReducer }
>(
    'projectProductTags/getByProjectProduct',
    async (projectProductId: number) => {
        const result = await http<any, undefined>({
            path: `${baseUrl}/project-product/${projectProductId}`,
            method: 'get',
        });
        const data = result.parsedBody?.data ?? result.parsedBody ?? [];
        return { projectProductId, tags: data };
    },
    {
        condition: (projectProductId, { getState }) => {
            const entry = getState().projectProductTagReducer.byProjectProductId[projectProductId];
            if (entry?.fetchedAt && Date.now() - entry.fetchedAt < PROJECT_PRODUCT_TAGS_TTL_MS) {
                return false;
            }
            return true;
        },
    },
);

export const getProjectProductTagsByProjectProductIdsAction = createAsyncThunk<any, number[]>(
    'projectProductTags/getByProjectProducts',
    async (projectProductIds: number[]) => {
        const query = projectProductIds.map((id) => `ids=${id}`).join('&');
        const result = await http<any, undefined>({
            path: `${baseUrl}/project-products?${query}`,
            method: 'get',
        });
        return result.parsedBody?.data ?? result.parsedBody ?? [];
    },
);

export const createProjectProductTagAction = createAsyncThunk<
    any,
    { projectProductId: number; tagId: number }
>(
    'projectProductTags/create',
    async ({ projectProductId, tagId }) => {
        const result = await http<any, { projectProductId: number; tagId: number }>({
            path: `${baseUrl}`,
            method: 'post',
            body: { projectProductId, tagId },
        });
        const created = result.parsedBody?.data ?? result.parsedBody;
        return { projectProductId, created };
    },
);

export const deleteProjectProductTagAction = createAsyncThunk<any, number>(
    'projectProductTags/delete',
    async (id: number) => {
        await http<any, number>({
            path: `${baseUrl}/${id}`,
            method: 'delete',
        });
        return { id };
    },
);

export const bulkDeleteProjectProductTagsAction = createAsyncThunk<void, number[]>(
    'projectProductTags/bulkDelete',
    async (ids: number[]) => {
        await Promise.all(
            ids.map((id) =>
                http<any, undefined>({
                    path: `${baseUrl}/${id}`,
                    method: 'delete',
                }),
            ),
        );
    },
);
